/**
 * MemoForm's orchestration layer: the due-value notification effect, the
 * field-level derivations (effective stage, owner, kind-driven copy,
 * title-row visibility), and the input handlers that translate UI events
 * into `useMemoFormState` setters.
 *
 * Extracted from MemoForm so the component itself reads as
 * state + submit wiring + layout composition, with this hook owning
 * everything that sits between them.
 */

import type { Membership } from 'api-client';
import { useCallback, useEffect, useMemo } from 'react';
import type { DateValue, TimeValue } from 'react-aria-components';

import type { StageAssociationValue } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';

import type { MemoFormControlsOwner } from './MemoFormControls';
import { combineDue } from './memoFormValues';
import { hasMeaningfulTitle, noteKindLabel } from './noteKind';
import type { MemoPrivacy } from './notes.utils';
import { isOwnerOrCreator } from './notes.utils';
import type { Note } from './useMemoRecords';

export interface UseMemoFormOrchestrationOptions {
  mode: 'create' | 'edit';
  note: Note | undefined;
  /** `entityType === 'ahoy_note'` — notes take no title/due prefills. */
  isNote: boolean;
  isInline: boolean;
  currentUserEntityId: string | undefined;
  users: Membership[] | undefined;
  orgId: number | undefined;
  ownerEntityId: string;
  setOwnerEntityId: (value: string) => void;
  setTitle: (value: string) => void;
  setDate: (value: string) => void;
  setTime: (value: TimeValue | null) => void;
  setPrivacy: (value: MemoPrivacy) => void;
  date: string;
  time: TimeValue | null;
  /** `undefined` = no pending override; the saved stage stands. */
  stagePickerOverride: string | null | undefined;
  setStagePickerOverride: (value: string | null | undefined) => void;
  /** Create-mode stage prefill; ignored in edit mode and for notes. */
  initialStageEntityId: string | null | undefined;
  onDueChange?: (due: string | undefined) => void;
}

export interface UseMemoFormOrchestrationResult {
  /** Pending picker override (tasks) merged over the derived saved stage. */
  effectiveStageAssociation: string | null;
  owner: MemoFormControlsOwner;
  canTogglePrivacy: boolean;
  noteKind: 'note' | 'task';
  submitLabel: string;
  titlePlaceholder: string;
  contentPlaceholder: string;
  /** Tasks always show it; notes only when a LEGACY note has a real title. */
  showTitleInput: boolean;
  showTitleRow: boolean;
  handleTitleChange: (value: string) => void;
  handleDateChange: (newValue: DateValue | null) => void;
  handleTimeChange: (newValue: TimeValue | null) => void;
  handlePrivacyChange: (isShared: boolean) => void;
  handleStagePickerChange: (assoc: StageAssociationValue | null) => void;
}

export function useMemoFormOrchestration({
  mode,
  note,
  isNote,
  isInline,
  currentUserEntityId,
  users,
  orgId,
  ownerEntityId,
  setOwnerEntityId,
  setTitle,
  setDate,
  setTime,
  setPrivacy,
  date,
  time,
  stagePickerOverride,
  setStagePickerOverride,
  initialStageEntityId,
  onDueChange,
}: UseMemoFormOrchestrationOptions): UseMemoFormOrchestrationResult {
  // Publish the resolved due (date, or date+time) to the parent whenever it
  // changes — drives the calendar's live "ghost" reminder marker. Mirrors the
  // value handed to onSubmit (combineDue): undefined / YYYY-MM-DD / ISO.
  useEffect(() => {
    onDueChange?.(combineDue(date, time));
  }, [date, time, onDueChange]);

  const stageAssociation =
    note?.stage_entity_id ??
    (mode === 'create' && !isNote ? (initialStageEntityId ?? null) : null);
  // Effective stage value used for submit and isEditDirty: a pending picker
  // selection (tasks only) overrides the derived stage.
  const effectiveStageAssociation =
    stagePickerOverride !== undefined ? stagePickerOverride : stageAssociation;

  const ownerMember = useMemo(
    () => users?.find(u => u.entityId === ownerEntityId),
    [users, ownerEntityId]
  );

  // react-aria's Input reports the raw string, not a ChangeEvent.
  const handleTitleChange = useCallback(
    (value: string) => setTitle(value),
    [setTitle]
  );

  const handleDateChange = useCallback(
    (newValue: DateValue | null) => setDate(newValue?.toString() ?? ''),
    [setDate]
  );

  const handleTimeChange = useCallback(
    (newValue: TimeValue | null) => setTime(newValue),
    [setTime]
  );

  // Privacy toggle handler. Stores the new value locally; the diff against
  // the saved `note.is_private` is computed by the consumer of `onSubmit`
  // (NoteRecordOverview's handleEdit calls the togglePrivacy endpoint when
  // the values differ).
  const handlePrivacyChange = useCallback(
    (isShared: boolean) => {
      setPrivacy(isShared ? 'shared' : 'private');
    },
    [setPrivacy]
  );

  // Stage picker handler for the metadata-bar task stage control.
  // Extracts the target_entity_id and stores it as the pending override.
  const handleStagePickerChange = useCallback(
    (assoc: StageAssociationValue | null) => {
      setStagePickerOverride(assoc?.target_entity_id ?? null);
    },
    [setStagePickerOverride]
  );

  // Only the owner or creator can flip privacy after creation; mirrors the
  // backend's privacy-toggle auth rule (see apps/crm_events/views/notes.py).
  // For create mode there's no `note` yet, so the current user is the de
  // facto creator and can always pick a starting visibility.
  // Applies to notes AND tasks: the privacy endpoint resolves the entity's
  // memo kind server-side (resolve_memo_visibility_names), so a task toggles
  // exactly like a note.
  const canTogglePrivacy =
    mode === 'create' ||
    (note != null &&
      currentUserEntityId != null &&
      isOwnerOrCreator(note, currentUserEntityId));

  // The composer's kind follows the entity type it creates/edits: the task
  // composer is always a task (task-ness is the entity type, never inferred
  // from stage presence — epic #13640) and the note composer is always a
  // note — notes no longer carry a due, so the dated-note "reminder" kind
  // never applies here (a legacy dated note still edits as a note). The
  // title/content placeholders and the create submit label follow that name.
  const noteKind = isNote ? 'note' : 'task';
  let submitLabel = 'Save';
  if (mode === 'create') {
    submitLabel = `Add ${noteKind}`;
  }
  const titlePlaceholder = `${noteKindLabel(noteKind)} title`;
  const contentPlaceholder = `Write a ${noteKind}`;
  // Tasks always have a title field. Notes only show one when a LEGACY note
  // already carries a real title (new notes are created without one).
  const showTitleInput =
    !isNote || hasMeaningfulTitle(mode === 'edit' ? note?.title : '');

  // Inline (record slideout): the privacy/owner cluster is NOT part of the
  // title row — it sits at the end of the attribution row below the body,
  // exactly where NoteCard's read view shows it — so the body's top edge is
  // the same in read and edit (no jump on click-in). A title-less note then
  // has no title row at all; a titled one keeps a 24px title-only row.
  const showTitleRow = !isInline || showTitleInput;

  const owner = useMemo<MemoFormControlsOwner>(
    () => ({
      kind: 'picker',
      users,
      ownerMember,
      orgId,
      onSelect: setOwnerEntityId,
    }),
    [users, ownerMember, orgId, setOwnerEntityId]
  );

  return {
    effectiveStageAssociation,
    owner,
    canTogglePrivacy,
    noteKind,
    submitLabel,
    titlePlaceholder,
    contentPlaceholder,
    showTitleInput,
    showTitleRow,
    handleTitleChange,
    handleDateChange,
    handleTimeChange,
    handlePrivacyChange,
    handleStagePickerChange,
  };
}
